import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, Nav, NavItem, NavLink, CardBody, Button, Col, Input, Row, Label, ModalBody, Form, TabPane, TabContent, Spinner, FormFeedback, FormGroup, Alert, CardFooter, Modal, ModalHeader } from 'reactstrap';
import OSelect from '../../../Components/OElements/OSelect';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import ListFlowRatesModal from '../Modals/ListFlowRatesModal';
import DeleteGridItemModal from '../Modals/DeleteGridItemModal';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { addNewWaterFlowRateList, updateWaterFlowRateList, deleteWaterFlowRateList } from '../../../slices/thunks';
import { asArray } from '../../../utils/asArray';
import { normalizeFlowRatePayload } from '../../../utils/normalizeFlowRatePayload';

const ListOfFlowRates = ({ flowRatesList, setFlowRatesList, formIsEdit, waterRightId, selectedWaterSourceIds = [] }) => {
	const dispatch = useDispatch();

	// Selectores para los datos maestros
	const selectorRightExerciseType = createSelector(
		state => state.RightExerciseType,
		data => asArray(data?.itemRightExerciseTypeList)
	);
	const rightExerciseTypeList = useSelector(selectorRightExerciseType);

	const selectorFlowRateUnit = createSelector(
		state => state.FlowRateUnit,
		data => asArray(data?.itemFlowRateUnitList)
	);
	const flowRateUnitList = useSelector(selectorFlowRateUnit);

	const selectorWaterSource = createSelector(
		state => state.WaterSource,
		data => asArray(data?.itemWaterSourceList)
	);
	const waterSourceList = useSelector(selectorWaterSource);

	// Helper functions to get names from IDs
	const getRightExerciseTypeName = (id) => {
		const item = rightExerciseTypeList.find(item => item._id === id);
		return item?.name || id || '-';
	};

	const getFlowRateUnitName = (id) => {
		const item = flowRateUnitList.find(item => item._id === id);
		return item?.name || id || '-';
	};

	const getWaterSourceName = (id) => {
		const item = waterSourceList.find(item => item._id === id);
		return item?.name || id || '-';
	};

	const [dataParticipant, setDataParticipant] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
	const [modal, setModal] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

    const [toggleTab, setToggleTab] = useState(null);

	// Get current user from localStorage
	const getCurrentUser = () => {
		try {
			const authUser = JSON.parse(localStorage.getItem('authUser'));
			return authUser?.user?.email || authUser?.email || 'system';
		} catch (error) {
			console.error('Error getting current user:', error);
			return 'system';
		}
	};

	const localKey = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

	const sameFlowRateItem = (a, b) => {
		if (a._id && b._id) return a._id === b._id;
		if (a._localKey && b._localKey) return a._localKey === b._localKey;
		return a === b;
	};

	// Handle Save (Add or Edit)
	const handleSave = async (formData) => {
		const payload = normalizeFlowRatePayload(formData);
		try {
			const currentUser = getCurrentUser();

			if (!formIsEdit) {
				// CREATE mode: no API calls, keep in local state until form is submitted
				if (isEdit && dataParticipant) {
					setFlowRatesList(prevList =>
						prevList.map(item =>
							sameFlowRateItem(item, dataParticipant)
								? { ...payload, _localKey: item._localKey, _id: item._id }
								: item
						)
					);
				} else {
					setFlowRatesList(prevList => [...prevList, { ...payload, _localKey: localKey() }]);
				}
			} else if (isEdit && dataParticipant?._id) {
				// EDIT mode: update existing item in API
				const updatePayload = {
					...payload,
					_id: dataParticipant._id,
					modification_user: currentUser,
					modification_date: new Date().toISOString()
				};

				const response = await dispatch(updateWaterFlowRateList(updatePayload)).unwrap();

				// Update local state with response from API
				setFlowRatesList(prevList =>
					prevList.map(item =>
						item._id === dataParticipant._id ? response : item
					)
				);
			} else {
				// EDIT mode: add new item to API immediately with water_right_id
				const createPayload = {
					...payload,
					water_right_id: waterRightId,
					creation_user: currentUser,
					creation_date: new Date().toISOString()
				};

				const response = await dispatch(addNewWaterFlowRateList(createPayload)).unwrap();

				// Add to local state with response from API (includes _id)
				setFlowRatesList(prevList => [...prevList, response]);
			}
			handleToggleModal();
		} catch (error) {
			console.error('Error saving flow rate:', error);
			// Toast notification is already handled in the thunk
		}
	};

	// Delete Data
	const handleDelete = async () => {
		setIsDeleting(true);
		try {
			if (dataParticipant?._id) {
				// If it has _id, it's saved in the database, so delete from API
				await dispatch(deleteWaterFlowRateList(dataParticipant)).unwrap();
				
				setFlowRatesList(prevList => 
					prevList.filter(item => item._id !== dataParticipant._id)
				);
			} else {
				// If no _id, just remove from local state (not saved yet)
				setFlowRatesList(prevList =>
					prevList.filter(item => !sameFlowRateItem(item, dataParticipant))
				);
			}
			handleCloseDeleteModal();
		} catch (error) {
			console.error('Error deleting flow rate:', error);
			// Toast notification is already handled in the thunk
		} finally {
			setIsDeleting(false);
		}
	};

	// Update Data
	const handleClickEdit = participant => {
		setIsEdit(true);
		setDataParticipant(participant);
		setModal(true);
	};

	// Delete Data
	const handleClickDelete = participant => {
		setDataParticipant(participant);
		setModalDelete(true);
	};

    function handleToggleModal() {
		setModal(prev => !prev);
	}

	function handleCloseDeleteModal() {
		setModalDelete(false);
	}

	const formatMonthCell = ({ cell }) => {
		const v = cell.getValue();
		return v === null || v === undefined || v === '' ? '-' : String(v);
	};

    const columns = useMemo(
		() => [
			{
				header: 'Acciones', enableSorting: false, enableColumnFilter: false, enableGlobalFilter: false, enableColumnActions: false, enableSorting: false, enableColumnFilter: false, enableGlobalFilter: false, enableColumnActions: false,
				accessorKey: 'residue',
				columnDefType: 'display',
				 size: 'auto',
				Cell: cellProps => {
					return (
						<React.Fragment>
							<div className='d-flex justify-content-center'>
								<div className='flex-shrink-0'>
									<ul className='list-inline mb-0'>
										<li className='list-inline-item'>
										<a
											title={'Editar'}
											href='#'
											onClick={(e) => {
												e.preventDefault();
												handleClickEdit(cellProps.row.original);
											}}
											style={{ cursor: 'pointer' }}
										>
											<i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
										</a>
									</li>
									<li className='list-inline-item'>
										<a
											title={'Eliminar'}
											href='#'
											className='remove-item-btn'
											onClick={(e) => {
												e.preventDefault();
												handleClickDelete(cellProps.row.original);
											}}
										>
											<i className='ri-delete-bin-line align-bottom me-2 text-muted'></i>
										</a>
										</li>
									</ul>
								</div>
							</div>
						</React.Fragment>
					);
				},
			},
			{ 
				header: 'Ejercicio del Derecho', 
				accessorKey: 'right_exercise_type_id',  
				size: 'auto',
				Cell: ({ row }) => row.original.right_exercise_type?.name || getRightExerciseTypeName(row.original.right_exercise_type_id) || '-'
			},
			{ 
				header: 'Unidad de Agua Autorizada', 
				accessorKey: 'flow_rate_unit_id',  
				size: 'auto',
				Cell: ({ row }) => row.original.flow_rate_unit?.name || getFlowRateUnitName(row.original.flow_rate_unit_id) || '-'
			},
			{ 
				header: 'Fuente', 
				accessorKey: 'water_source_id',  
				size: 'auto',
				Cell: ({ row }) => row.original.water_source?.name || getWaterSourceName(row.original.water_source_id) || '-'
			},
			{ header: 'Enero', accessorKey: 'january', size: 'auto', Cell: formatMonthCell },
			{ header: 'Febrero', accessorKey: 'february', size: 'auto', Cell: formatMonthCell },
			{ header: 'Marzo', accessorKey: 'march', size: 'auto', Cell: formatMonthCell },
			{ header: 'Abril', accessorKey: 'april', size: 'auto', Cell: formatMonthCell },
			{ header: 'Mayo', accessorKey: 'may', size: 'auto', Cell: formatMonthCell },
			{ header: 'Junio', accessorKey: 'june', size: 'auto', Cell: formatMonthCell },
			{ header: 'Julio', accessorKey: 'july', size: 'auto', Cell: formatMonthCell },
			{ header: 'Agosto', accessorKey: 'august', size: 'auto', Cell: formatMonthCell },
			{ header: 'Septiembre', accessorKey: 'september', size: 'auto', Cell: formatMonthCell },
			{ header: 'Octubre', accessorKey: 'october', size: 'auto', Cell: formatMonthCell },
			{ header: 'Noviembre', accessorKey: 'november', size: 'auto', Cell: formatMonthCell },
			{ header: 'Diciembre', accessorKey: 'december', size: 'auto', Cell: formatMonthCell },
		],
		[rightExerciseTypeList, flowRateUnitList, waterSourceList]
	);

    return (
        <React.Fragment>
			<Col lg={12}>
                <div className='d-flex align-items-center'>
                    <h5 className='card-title mb-0 flex-grow-1 '>
                        Lista de Caudales
                    </h5>
                    <div className='flex-shrink-0'>
                        <div className='d-flex flex-wrap gap-2'>
                            <button
                                className='btn btn-label btn-primary'
                                onClick={() => {
                                    setIsEdit(false);
                                    setDataParticipant({});
                                    handleToggleModal();
                                }}
                                type='button'
                            >
                                <i className='ri-add-line label-icon'></i> Añadir Lista Caudales
                            </button>
                        </div>
                    </div>
                </div>
                <div className='mt-3 my-4'>
                    <ODataGrid
                        columns={columns}
                        data={flowRatesList || []}
                    />
                </div>
            </Col>
			<ListFlowRatesModal
                dataParticipant={dataParticipant}
                isOpen={modal}
                isEdit={isEdit}
                handleToggleModal={handleToggleModal}
                handleSave={handleSave}
                selectedWaterSourceIds={selectedWaterSourceIds}
            />
			
			<DeleteGridItemModal
				isOpen={modalDelete}
				title='¡Estás a punto de eliminar una Lista de Caudales!'
				description='Si deseas realizar esta acción debes hacer click en confirmar.'
				details={dataParticipant && (
					<div className='small text-muted'>
						<p><strong>Ejercicio del Derecho:</strong> {getRightExerciseTypeName(dataParticipant.right_exercise_type_id)}</p>
						<p><strong>Unidad:</strong> {getFlowRateUnitName(dataParticipant.flow_rate_unit_id)}</p>
						<p><strong>Fuente:</strong> {getWaterSourceName(dataParticipant.water_source_id)}</p>
					</div>
				)}
				onCancel={handleCloseDeleteModal}
				onConfirm={handleDelete}
				isDeleting={isDeleting}
			/>
        </React.Fragment>
    )
}

export default ListOfFlowRates;